import React from "react";
import { MapPin, ExternalLink } from "lucide-react";

/**
 * GMapEmbed — keyless Google Maps embed via iframe.
 *
 * Uses the public `maps.google.com/maps?q=...&output=embed` endpoint that
 * does NOT require an API key (no billing, no quota). Renders a single pin
 * at the given (lat, lng). For multi-pin maps fall back to the static
 * "open in Google Maps" link below the iframe.
 *
 * Props:
 *   - lat, lng   : required scalar coordinates
 *   - zoom       : 10–18 (default 15)
 *   - label      : optional caption shown under the map
 *   - height     : tailwind h-* class (default h-[280px])
 *   - testId     : data-testid prefix
 */
export default function GMapEmbed({ lat, lng, zoom = 15, label, height = "h-[280px]", testId = "gmap" }) {
  if (lat == null || lng == null || isNaN(Number(lat)) || isNaN(Number(lng))) {
    return (
      <div className={`${height} rounded-xl bg-stone-100 border border-dashed border-stone-300 flex flex-col items-center justify-center text-stone-500 text-sm`} data-testid={`${testId}-empty`}>
        <MapPin size={28} className="mb-2 opacity-50" />
        <div>لا توجد إحداثيات لعرض الخريطة</div>
      </div>
    );
  }
  const src = `https://maps.google.com/maps?q=${lat},${lng}&z=${zoom}&output=embed`;
  const openLink = `https://www.google.com/maps?q=${lat},${lng}`;
  return (
    <div className="relative" data-testid={testId}>
      <div className={`${height} rounded-xl overflow-hidden border border-stone-200 bg-stone-100`}>
        <iframe
          title="Salon location"
          src={src}
          width="100%"
          height="100%"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          style={{ border: 0, display: "block" }}
          allowFullScreen
          data-testid={`${testId}-iframe`}
        />
      </div>
      <a
        href={openLink}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute bottom-2 end-2 bg-white/95 hover:bg-white shadow-md rounded-full px-3 py-1.5 text-xs font-bold text-stone-800 flex items-center gap-1.5 transition"
        data-testid={`${testId}-open-link`}
      >
        <ExternalLink size={12} />
        فتح في خرائط جوجل
      </a>
      {label && (
        <div className="mt-2 text-xs text-stone-500 text-center">{label}</div>
      )}
    </div>
  );
}
